import { Controller } from "@hotwired/stimulus"

// Toggles a class (default: 'shadow') when a sticky header actually sticks to the top
// Usage in markup:
//   data-controller="sticky-header"
//   data-sticky-header-stuck-class="shadow"   # optional; defaults to 'shadow-lg' here
export default class extends Controller {
  static classes = ["stuck"]

  connect() {
    this._stuckClass = this.hasStuckClass ? this.stuckClass : "shadow"

    // Insert a 1px sentinel just before the element that doesn't affect layout
    this._sentinel = document.createElement("div")
    this._sentinel.setAttribute("aria-hidden", "true")
    this._sentinel.style.height = "1px"
    this._sentinel.style.marginBottom = "-1px" // cancels height so no layout shift
    this.element.before(this._sentinel)

    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.element.classList.remove(this._stuckClass)
        } else {
          this.element.classList.add(this._stuckClass)
        }
      },
      {
        root: null,
        threshold: 0,
      }
    )
    this._observer.observe(this._sentinel)

    // Keep a CSS variable updated with the current header height so other sticky elements
    // (like tabs) can offset themselves to sit just below the header.
    this._updateHeaderHeight = this._updateHeaderHeight.bind(this)
    this._updateHeaderHeight()

    // Update on window resize and after page load (fonts/images can affect height)
    window.addEventListener("resize", this._updateHeaderHeight)
    window.addEventListener("orientationchange", this._updateHeaderHeight)
    window.addEventListener("load", this._updateHeaderHeight)

    // Observe this header element for size changes (safer than polling)
    this._resizeObserver = new ResizeObserver(this._updateHeaderHeight)
    this._resizeObserver.observe(this.element)
  }

  disconnect() {
    this._observer?.disconnect()
    this._sentinel?.remove()
    this._resizeObserver?.disconnect()
    window.removeEventListener("resize", this._updateHeaderHeight)
    window.removeEventListener("orientationchange", this._updateHeaderHeight)
    window.removeEventListener("load", this._updateHeaderHeight)
  }

  _updateHeaderHeight() {
    // Use offsetHeight to include padding and border
    const height = this.element?.offsetHeight || 0
    // Set on :root so any element can consume it via var(--sticky-header-height)
    document.documentElement.style.setProperty("--sticky-header-height", `${height}px`)
  }
}
